import { BrowserRouter, Route, Routes } from "react-router";
import { AppWrapper } from "./AppWrapper";
import { RouteEnum } from "../common/enums/RouteEnum";
import DashboardPageContainer from "../pages/dashboardPage/DashboardPageContainer";
import CertificatePageContainer from "../pages/certificatePage/CertificatePageContainer";
import QualificationDetailPageContainer from "../pages/qualificationDetailPage/QualificationDetailPageContainer";
import QualificationListPageContainer from "../pages/qualificationListPage/QualificationListPageContainer";
import { JSX, useEffect } from "react";
import { meCannotEnterPortal } from "../common/utils/resolveStartUrl";
import {
    setEmployeeId,
    setEstablishmentId,
    unsetEstablishmentId,
} from "./rootReducer";
import { useGetEmployeeMeQuery } from "../common/api/employee";
import { useAppDispatch } from "./hooks";
import { useEstablishmentId } from "../common/utils/useEstablishmentId";
import DataManagementPageContainer from "../pages/dataManagementPage/DataManagementPageContainer";
import { ReviewPageContainer } from "../pages/reviewPage/ReviewPageContainer";
import { EmployeeDetailPageContainer } from "../pages/employeeDetailPage/EmployeeDetailPageContainer";
import { useIsEstablishmentManager } from "../common/utils/useIsEstablishmentManager";
import { EmployeePageContainer } from "../pages/employeePage/EmployeePageContainer";
import NotificationsPageContainer from "../pages/notificationsPage/NotificationsPageContainer";
import AppLoader from "../common/components/AppLoader";
import { RedirectToStartCountdown } from "../common/components/RedirectToStartCountdown";
import AbsLogo from "../common/images/AbsLogo.svg";
import { StatusEnum } from "../common/enums/StatusEnum";
import { EmployeeInactivePage } from "../pages/employeeInactivePage/EmployeeInactivePage";
import { Bounce, ToastContainer } from "react-toastify";
import { useIsEstablishmentSet } from "../common/utils/useIsEstablishmentSet";
import { EstablishmentsPageContainer } from "../pages/establishmentsPage/EstablishmentsPageContainer";
import { useEmployeeId } from "../common/utils/useEmployeeId";
import { useIsGroupManager } from "../common/utils/useIsGroupManager";
import { ScheduledQualificationsPageContainer } from "../pages/scheduledQualificationsPage/ScheduledQualificationsPageContainer";
import { CertificateExpiryPageContainer } from "../pages/certificateExpiryPage/CertificateExpiryPageContainer";
import ShowEstablishmentDetailsPageContainer from "../pages/showEstablishmentDetailsPage/ShowEstablishmentDetailsPageContainer";
import { useRole } from "../common/utils/useRole";
import { adminAppEntryUrl } from "../common/configs/keycloakAuth";

const App = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const {
        data: employeeMeData,
        isLoading: meLoading,
        isUninitialized: meUninitialized,
        isError: employeeMeDataIsError,
    } = useGetEmployeeMeQuery({});
    const establishmentId = useEstablishmentId();
    const employeeId = useEmployeeId();
    const isEstablishmentManager = useIsEstablishmentManager();
    const isEstablishmentSet = useIsEstablishmentSet();
    const isGroupManager = useIsGroupManager();
    const { shouldLeaveForAdminApp } = useRole();
    // /me returns 200 for authenticated users without a portal employee
    // (CRM-only, no LMS roles). Do not wait for establishment/employee ids.
    const cannotEnterPortal =
        meCannotEnterPortal(employeeMeData) && !shouldLeaveForAdminApp;

    useEffect(() => {
        if (shouldLeaveForAdminApp) {
            window.location.href = adminAppEntryUrl(
                import.meta.env.VITE_ADMIN_URL,
                import.meta.env.VITE_AUTH_PROVIDER,
            );
        }
    }, [shouldLeaveForAdminApp]);

    useEffect(() => {
        if (!isGroupManager && employeeMeData) {
            dispatch(unsetEstablishmentId());
        }

        if (employeeMeData?.id != null) {
            dispatch(setEmployeeId(employeeMeData.id));
        }

        if (
            employeeMeData &&
            employeeMeData.establishment &&
            !isEstablishmentSet
        ) {
            dispatch(setEstablishmentId(employeeMeData.establishment.id));
        }
    }, [dispatch, employeeMeData, isEstablishmentSet, isGroupManager]);

    if (employeeMeDataIsError || cannotEnterPortal) {
        return (
            <RedirectToStartCountdown
                logoSrc={AbsLogo}
                startUrl={import.meta.env.VITE_START_URL}
            />
        );
    }

    if (
        meLoading ||
        meUninitialized ||
        !employeeMeData ||
        !establishmentId ||
        !employeeId ||
        shouldLeaveForAdminApp
    ) {
        return <AppLoader logoSrc={AbsLogo} />;
    }

    return (
        <>
            <ToastContainer
                className={"c-toast"}
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
            />
            {employeeMeData.status.code === StatusEnum.INACTIVE ? (
                <EmployeeInactivePage />
            ) : (
                <BrowserRouter>
                    <AppWrapper>
                        <Routes>
                            <Route index element={<DashboardPageContainer />} />
                            <Route
                                path={RouteEnum.NOTIFICATIONS}
                                element={<NotificationsPageContainer />}
                            />
                            <Route
                                path={RouteEnum.DASHBOARD}
                                element={<DashboardPageContainer />}
                            />
                            <Route
                                path={RouteEnum.QUALIFICATIONS}
                                element={<QualificationListPageContainer />}
                            />
                            <Route
                                path={RouteEnum.DATA_MANAGEMENT}
                                element={<DataManagementPageContainer />}
                            />
                            <Route
                                path={
                                    RouteEnum.QUALIFICATIONS +
                                    "/:qualificationId"
                                }
                                element={<QualificationDetailPageContainer />}
                            />
                            <Route
                                path={RouteEnum.SCHEDULED_QUALIFICATIONS}
                                element={
                                    <ScheduledQualificationsPageContainer />
                                }
                            />
                            <Route
                                path={RouteEnum.CERTIFICATE_EXPIRY}
                                element={<CertificateExpiryPageContainer />}
                            />
                            {isEstablishmentManager ? (
                                <>
                                    <Route
                                        path={RouteEnum.REVIEWS}
                                        element={<ReviewPageContainer />}
                                    />
                                    <Route
                                        path={RouteEnum.ESTABLISHMENTS}
                                        element={
                                            <EstablishmentsPageContainer />
                                        }
                                    />
                                    <Route
                                        path={RouteEnum.EMPLOYEES}
                                        element={<EmployeePageContainer />}
                                    />
                                    <Route
                                        path={
                                            RouteEnum.EMPLOYEES + "/:employeeId"
                                        }
                                        element={
                                            <EmployeeDetailPageContainer />
                                        }
                                    />
                                    <Route
                                        path={
                                            RouteEnum.SHOW_ESTABLISHMENT_DETAILS
                                        }
                                        element={
                                            <ShowEstablishmentDetailsPageContainer />
                                        }
                                    />
                                </>
                            ) : (
                                <Route
                                    path={RouteEnum.ACHIEVEMENTS}
                                    element={<CertificatePageContainer />}
                                />
                            )}
                        </Routes>
                    </AppWrapper>
                </BrowserRouter>
            )}
        </>
    );
};

export default App;
